"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.selectValidUserRoles = selectValidUserRoles;
exports.selectValidTimetable = selectValidTimetable;
exports.verifyUnlockRequest = verifyUnlockRequest;
var hasura_1 = require("../hasura");
var main_api_1 = require("../main-api");
var timetable_verification_1 = require("../timetable-verification");
var utils_1 = require("../utils");
function isAccessPointWithinScope(accessPoint, userRole) {
    var accessPointId = accessPoint.accessPointId, property = accessPoint.property;
    var role = userRole.role, scopedOrganizationId = userRole.scopedOrganizationId, scopedPropertyId = userRole.scopedPropertyId, scopedUnit = userRole.scopedUnit, scopedAccessPointId = userRole.scopedAccessPointId;
    switch (role.permissionScope) {
        case hasura_1.PermissionScope_enum.GLOBAL:
            return true;
        case hasura_1.PermissionScope_enum.ORGANIZATION:
            return scopedOrganizationId === property.organizationId;
        case hasura_1.PermissionScope_enum.PROPERTY:
            return scopedPropertyId === property.propertyId;
        case hasura_1.PermissionScope_enum.UNIT:
            return (scopedUnit === null || scopedUnit === void 0 ? void 0 : scopedUnit.propertyId) === property.propertyId;
        case hasura_1.PermissionScope_enum.ACCESS_POINT:
            return scopedAccessPointId === accessPointId;
        default:
            return false;
    }
}
function getAncestorUnitGroups(unitGroup, propertyUnitGroups, ancestorUnitGroups) {
    if (ancestorUnitGroups === void 0) { ancestorUnitGroups = []; }
    var parentUnitGroupId = unitGroup.parentUnitGroupId;
    var parentUnitGroup = parentUnitGroupId
        ? propertyUnitGroups.find(function (u) { return u.unitGroupId === parentUnitGroupId; })
        : null;
    if (parentUnitGroup) {
        ancestorUnitGroups.push(parentUnitGroup);
        return getAncestorUnitGroups(parentUnitGroup, propertyUnitGroups, ancestorUnitGroups);
    }
    return ancestorUnitGroups;
}
function isUnitGroupWithinScope(unitGroupId, unitGroup) {
    if (unitGroup.unitGroupId === unitGroupId) {
        return true;
    }
    var ancestorUnitGroups = getAncestorUnitGroups(unitGroup, unitGroup.property.unitGroups);
    return ancestorUnitGroups.some(function (u) { return u.unitGroupId === unitGroupId; });
}
function isWhitelistedUserRole(accessPointRole, accessPoint, userRole) {
    var hasAccess = accessPointRole.hasAccess, roleId = accessPointRole.roleId, unitGroupId = accessPointRole.unitGroupId;
    var role = userRole.role, scopedUnit = userRole.scopedUnit;
    if (!hasAccess || roleId !== role.roleId) {
        // Role is either blacklisted (ignored) or whitelisted but doesn't match the user role
        return false;
    }
    if (!unitGroupId) {
        // User role is whitelisted as long as the AP is within scope
        return isAccessPointWithinScope(accessPoint, userRole);
    }
    if (!(scopedUnit === null || scopedUnit === void 0 ? void 0 : scopedUnit.unitGroup)) {
        // User role is not whitelisted. Access point role is configured for specific unit group
        return false;
    }
    return isUnitGroupWithinScope(unitGroupId, scopedUnit.unitGroup);
}
function isBlacklistedUserRole(accessPointRole, userRole) {
    var hasAccess = accessPointRole.hasAccess, roleId = accessPointRole.roleId, unitGroupId = accessPointRole.unitGroupId;
    var role = userRole.role, scopedUnit = userRole.scopedUnit;
    if (hasAccess || roleId !== role.roleId) {
        // Role is either whitelisted (ignored) or blacklisted but doesn't match the user role
        return false;
    }
    if (!unitGroupId) {
        // User role is blacklisted because the role matches and there is no unit group specified
        return true;
    }
    if (!(scopedUnit === null || scopedUnit === void 0 ? void 0 : scopedUnit.unitGroup)) {
        // User role is not blacklisted. Access point role is configured for specific unit group
        return false;
    }
    return isUnitGroupWithinScope(unitGroupId, scopedUnit.unitGroup);
}
function selectValidUserRoles(accessPoint, userRoles, options) {
    if (options === void 0) { options = { includeReservationOnly: false }; }
    var validUserRoles = userRoles.filter(function (u) { return (u.status === hasura_1.UserRoleStatus_enum.ACTIVE &&
        u.userRolePermissions.some(function (p) { return p.permissionKey === hasura_1.PermissionKey_enum.AccessPoint_Unlock; })); });
    if (!validUserRoles.length) {
        return [];
    }
    var type = accessPoint.type;
    validUserRoles = validUserRoles.filter(function (userRole) {
        var withinScope = isAccessPointWithinScope(accessPoint, userRole);
        var hasStaffAccess = userRole.userRolePermissions.some(function (p) { return (p.permissionKey === hasura_1.PermissionKey_enum.AccessPoint_StaffAccess); });
        if (withinScope && hasStaffAccess) {
            return true; // Bypasses all restrictions
        }
        // Include access points in the app for residents to create a reservation
        if (withinScope &&
            options.includeReservationOnly &&
            type === hasura_1.AccessPointType_enum.RESERVATION_ONLY &&
            userRole.role.key === hasura_1.RoleKey_enum.RESIDENT) {
            return true;
        }
        // Reservation and Amenity Access roles only apply to their respective AP types
        var isBasicAccessRole = userRole.role.key === hasura_1.RoleKey_enum.BASIC_ACCESS;
        var isReservationRole = userRole.role.key === hasura_1.RoleKey_enum.RESERVATION;
        var isAmenityAccessRole = userRole.role.key === hasura_1.RoleKey_enum.AMENITY_ACCESS;
        if (type === hasura_1.AccessPointType_enum.RESERVATION_ONLY) {
            return withinScope && isReservationRole;
        }
        if (type === hasura_1.AccessPointType_enum.AMENITY) {
            return withinScope && (isAmenityAccessRole || isBasicAccessRole);
        }
        if (userRole.role.permissionScope === hasura_1.PermissionScope_enum.ACCESS_POINT) {
            // All other roles scoped by access point are auto-whitelisted (e.g., "Basic Access")
            return withinScope && !isReservationRole && !isAmenityAccessRole;
        }
        var whitelisted = accessPoint.accessPointRoles.some(function (accessPointRole) { return (isWhitelistedUserRole(accessPointRole, accessPoint, userRole)); });
        // Check whitelist for cross-property access (via unit groups)
        if (type === hasura_1.AccessPointType_enum.COMMON_AREA) {
            return withinScope || whitelisted;
        }
        if (type === hasura_1.AccessPointType_enum.RESTRICTED_WHITELIST) {
            return whitelisted;
        }
        if (type === hasura_1.AccessPointType_enum.RESTRICTED_BLACKLIST) {
            return withinScope && !accessPoint.accessPointRoles.some(function (accessPointRole) { return (isBlacklistedUserRole(accessPointRole, userRole)); });
        }
        return false;
    });
    return validUserRoles;
}
function selectValidTimetable(accessPoint, userRoles, options) {
    if (options === void 0) { options = { includeReservationOnly: false, excludeBypassedSchedules: false }; }
    var timetable = accessPoint.timetable;
    var validUserRoles = selectValidUserRoles(accessPoint, userRoles, {
        includeReservationOnly: options.includeReservationOnly,
    });
    var canBypassSchedule = validUserRoles.some(function (u) { return (u.userRolePermissions.some(function (p) { return p.permissionKey === hasura_1.PermissionKey_enum.AccessPoint_BypassSchedule; })); });
    var uncontrolledSchedules = Array.isArray(timetable)
        ? timetable.filter(function (t) { return t.state !== main_api_1.TimetableRuleState.CONTROLLED; })
        : [];
    if (canBypassSchedule) {
        // 24/7 access; bypass all schedules when unlocking
        var filteredSchedules = options.excludeBypassedSchedules ? [] : uncontrolledSchedules;
        return filteredSchedules.length ? filteredSchedules : null;
    }
    var controlledSchedules = Array.isArray(timetable)
        ? timetable.filter(function (t) { return t.state === main_api_1.TimetableRuleState.CONTROLLED; })
        : [];
    var defaultSchedules = [];
    var combinedRoleSchedules = [];
    for (var _i = 0, validUserRoles_1 = validUserRoles; _i < validUserRoles_1.length; _i++) {
        var userRole = validUserRoles_1[_i];
        var roleSchedules = [];
        // @TODO: How should we handle multiple custom schedules for the same role,
        // but different unit group specifications (no unit group, child unit group, etc.)?
        // Right now we're just combining the custom schedules instead of selecting one.
        for (var _a = 0, _b = accessPoint.accessPointRoles; _a < _b.length; _a++) {
            var accessPointRole = _b[_a];
            var roleSchedule = accessPointRole.schedule;
            if (isWhitelistedUserRole(accessPointRole, accessPoint, userRole)) {
                if (roleSchedule) {
                    roleSchedules = __spreadArray(__spreadArray([], roleSchedules, true), roleSchedule.map(function (rule) { return (__assign(__assign({}, rule), { state: main_api_1.TimetableRuleState.CONTROLLED })); }), true);
                }
            }
        }
        if (roleSchedules.length) {
            // Apply custom role (CONTROLLED) schedules
            combinedRoleSchedules = __spreadArray(__spreadArray([], combinedRoleSchedules, true), roleSchedules, true);
        }
        else {
            if (!controlledSchedules.length) {
                // If no custom role schedules are applied AND there are no default CONTROLLED
                // schedules, the user has 24/7 access and no other user roles need to be checked.
                return uncontrolledSchedules.length ? uncontrolledSchedules : null;
            }
            // Apply default (CONTROLLED) schedules
            defaultSchedules = controlledSchedules;
        }
    }
    var allCombinedSchedules = __spreadArray(__spreadArray(__spreadArray([], defaultSchedules, true), combinedRoleSchedules, true), uncontrolledSchedules, true);
    return allCombinedSchedules.length ? allCombinedSchedules : null;
}
function verifyUnlockRequest(accessPoint, userRoles) {
    var timezone = accessPoint.property.timezone;
    var validUserRoles = selectValidUserRoles(accessPoint, userRoles);
    if (!validUserRoles.length) {
        throw new utils_1.ExposedError('You do not have access to this entry point');
    }
    validUserRoles = userRoles.filter(function (u) { return (!u.schedule || (0, timetable_verification_1.isDateAllowedOnTimetable)(new Date(), u.schedule, timezone)); });
    if (!validUserRoles.length) {
        throw new utils_1.ExposedError('You cannot access this entry point at this time');
    }
    var timetable = selectValidTimetable(accessPoint, validUserRoles, {
        includeReservationOnly: false,
        excludeBypassedSchedules: true,
    });
    if (timetable && !(0, timetable_verification_1.isDateAllowedOnTimetable)(new Date(), timetable, timezone)) {
        throw new utils_1.ExposedError('You cannot access this entry point at this time');
    }
    return { timetable: timetable, userRoles: validUserRoles };
}
//# sourceMappingURL=index.js.map